import React, { useState, useRef, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import OutlinedInput from "@mui/material/OutlinedInput";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";

import "./ModalAdicionarEvolucao.scss";
import toast from "react-hot-toast";
import {
  encontrarTratamentoEquivalente,
  motivoTratamentoSemEquivalente,
} from "../../../../shared/util/tratamentoEquivalente";
import { useLoading } from "../../../../shared/context/LoadingContext";
import { ClinicaContext } from "../../../../shared/context/clinica-context";
import NotaInformativa from "../../../../shared/components/notaInformativa/NotaInformativa";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const facesDentes = ["D", "M", "O/I", "P/L", "V"];

const todosOsDentes = [
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "21",
  "22",
  "23",
  "24",
  "25",
  "26",
  "27",
  "28",
  "31",
  "32",
  "33",
  "34",
  "35",
  "36",
  "37",
  "38",
  "41",
  "42",
  "43",
  "44",
  "45",
  "46",
  "47",
  "48",
  "51",
  "52",
  "53",
  "54",
  "55",
  "61",
  "62",
  "63",
  "64",
  "65",
  "71",
  "72",
  "73",
  "74",
  "75",
  "81",
  "82",
  "83",
  "84",
  "85",
  "Arcada Superior",
  "Arcada Inferior",
  "Arcadas",
];

function ModalExecutarTratamentoIsolado({
  handleCloseModal,
  open,
  dados,
  auth,
  sendRequest,
  handleModalSubmitSuccess,
}) {
  const clinica = useContext(ClinicaContext);
  const { startLoading, stopLoading } = useLoading();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const relatorioRef = useRef(null);

  // Estados para data/hora
  const [dataInicioExecucao, setDataInicioExecucao] = useState(
    moment(dados.data_inicio_execucao)
  );

  const [dataFimExecucao, setDataFimExecucao] = useState(
    moment(dados.data_fim_execucao)
  );

  // Estados para controle de tratamento
  const [selectedProcedure, setSelectedProcedure] = useState(
    dados.tratamento?._id || ""
  );

  const [tratamentos, setTratamentos] = useState([]);
  const [allTratamentos, setAllTratamentos] = useState([]);

  // Estados para dentes e faces
  const [dentes, setDentes] = useState(
    dados.dente ? dados.dente.split(",") : []
  );
  const [faces, setFaces] = useState(dados.faces || []);
  const [listaDentes, setListaDentes] = useState(todosOsDentes);
  const [showDentes, setShowDentes] = useState(false);
  const [showFaces, setShowFaces] = useState(false);

  // Estados para validação
  const [erroDentes, setErroDentes] = useState("");
  const [erroFaces, setErroFaces] = useState("");

  // Estados para controle de alteração
  const [tratamentoOriginal, setTratamentoOriginal] = useState(null);
  const [tratamentoAtual, setTratamentoAtual] = useState(null);
  const [tratamentoNovo, setTratamentoNovo] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [diferencaValor, setDiferencaValor] = useState(0);

  const [tratamentosAdicionais, setTratamentosAdicionais] = useState([]);

  useEffect(() => {
    if (open) {
      fetchTratamentos();
    }
  }, [open]);

  useEffect(() => {
    // Atualizar visibilidade e validação quando o tratamento atual mudar
    if (tratamentoAtual) {
      configurarCamposDentesEFaces(tratamentoAtual);

      // Se o tratamento aceita dentes e tem apenas 1 dente disponível, selecionar automaticamente
      if (tratamentoAtual.aceitaDentes && tratamentoAtual.dentesEspecificos) {
        const dentesDisponiveis = tratamentoAtual.dentesEspecificos
          .split(",")
          .map((d) => d.trim());
        if (dentesDisponiveis.length === 1) {
          setDentes(dentesDisponiveis);
          validarDentes(dentesDisponiveis, tratamentoAtual);
        }
      }
    }
  }, [tratamentoAtual]);

  const fetchTratamentos = async () => {
    try {
      startLoading();
      const tratamentosRetornados = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/clinica/${clinica.clinica._id}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      const tratamentosOrdenadosAlfabeticamente =
        tratamentosRetornados.tratamentos.sort((a, b) =>
          a.designacao.trim().localeCompare(b.designacao.trim())
        );

      const tempTratamentosAutocomplete =
        tratamentosOrdenadosAlfabeticamente.map((t) => ({
          id: t._id,
          label: t.fornecedor?.nome
            ? `${t.designacao.trim()} (${t.fornecedor.nome})`
            : t.designacao.trim(),
        }));

      setTratamentos(tempTratamentosAutocomplete);
      setAllTratamentos(tratamentosOrdenadosAlfabeticamente);

      // Guardar informações do tratamento original
      if (dados.tratamento) {
        /* Por id e, não achando, pelo NOME.
           Um tratamento de outra unidade é um documento diferente com id
           diferente — a procura por id devolvia `undefined` e o modal abria
           sem tratamento selecionado, sem dizer porquê. As guardas do ecrã já
           impedem chegar aqui de fora, mas se algum caminho escapar é melhor
           encontrar o equivalente do que abrir em branco. */
        const tratOriginal = encontrarTratamentoEquivalente(
          dados.tratamento,
          tratamentosOrdenadosAlfabeticamente,
        );
        if (!tratOriginal) {
          toast.error(motivoTratamentoSemEquivalente(dados.tratamento));
        }
        setTratamentoOriginal(tratOriginal);
        setTratamentoAtual(tratOriginal);
      } else {
        // Sem tratamento específico - apenas categoria (caso sem entrada)
        setTratamentoOriginal(null);
        setTratamentoAtual(null);
      }
    } catch (err) {
      console.error("Erro ao buscar tratamentos:", err);
    } finally {
      stopLoading();
    }
  };

  const configurarCamposDentesEFaces = (tratamento) => {
    if (!tratamento) return;

    // Configurar lista de dentes disponíveis
    if (tratamento.dentesEspecificos) {
      setListaDentes(tratamento.dentesEspecificos.split(", "));
    } else {
      setListaDentes(todosOsDentes);
    }

    // Configurar visibilidade de dentes
    setShowDentes(tratamento.aceitaDentes);

    // Configurar visibilidade de faces
    const dentesQueNaoAceitamFaces = [
      "Arcadas",
      "Arcada Superior",
      "Arcada Inferior",
    ];
    const dentesSelecionadosSaoArcadas = dentes.some((d) =>
      dentesQueNaoAceitamFaces.includes(d)
    );

    setShowFaces(
      tratamento.aceitaFaces &&
        !dentesSelecionadosSaoArcadas &&
        !(
          tratamento.dentesEspecificos &&
          dentesQueNaoAceitamFaces.includes(tratamento.dentesEspecificos)
        )
    );

    // Limpar seleções inválidas
    if (!tratamento.aceitaDentes) {
      setDentes([]);
      setErroDentes("");
    }

    if (!tratamento.aceitaFaces || dentesSelecionadosSaoArcadas) {
      setFaces([]);
      setErroFaces("");
    }

    // Validar seleções existentes
    validarDentes(dentes, tratamento);
    validarFaces(faces, tratamento);
  };

  const validarDentes = (dentesArray, tratamento) => {
    if (!tratamento || !tratamento.aceitaDentes) {
      setErroDentes("");
      return true;
    }

    if (dentesArray.length === 0) {
      setErroDentes("Por favor selecione os dentes a tratar");
      return false;
    }

    if (tratamento.multiDentes) {
      if (dentesArray.length % tratamento.qtdDentes !== 0) {
        setErroDentes(
          `O tratamento ${tratamento.designacao} requer múltiplos de ${tratamento.qtdDentes} dentes. Foram selecionados ${dentesArray.length} dentes.`
        );
        return false;
      }
    }

    setErroDentes("");
    return true;
  };

  const validarFaces = (facesArray, tratamento) => {
    if (!tratamento || !tratamento.aceitaFaces || !showFaces) {
      setErroFaces("");
      return true;
    }

    if (facesArray.length === 0) {
      setErroFaces("Por favor selecione as faces a tratar");
      return false;
    }

    if (tratamento.qtdFaces < 5 && facesArray.length !== tratamento.qtdFaces) {
      setErroFaces(
        `O tratamento ${tratamento.designacao} exige exatamente ${tratamento.qtdFaces} face(s). Foram selecionadas ${facesArray.length}.`
      );
      return false;
    }

    if (tratamento.qtdFaces === 5 && facesArray.length > 5) {
      setErroFaces("Não é possível selecionar mais de 5 faces");
      return false;
    }

    setErroFaces("");
    return true;
  };

  const dentesChangeHandler = (event) => {
    const {
      target: { value },
    } = event;
    const selectedDentes = typeof value === "string" ? value.split(",") : value;

    // Lógica especial para arcadas
    if (selectedDentes.includes("Arcadas")) {
      setDentes(["Arcadas"]);
      setFaces([]);
      setShowFaces(false);
    } else if (
      selectedDentes.includes("Arcada Superior") ||
      selectedDentes.includes("Arcada Inferior")
    ) {
      const apenasArcadas = selectedDentes.filter(
        (d) => d === "Arcada Superior" || d === "Arcada Inferior"
      );
      setDentes(apenasArcadas);
      setFaces([]);
      setShowFaces(false);
    } else {
      setDentes(selectedDentes);
      // Reconfigurar faces baseado no tratamento atual
      if (
        tratamentoAtual &&
        tratamentoAtual.aceitaFaces &&
        selectedDentes.length > 0
      ) {
        setShowFaces(true);
      }
    }

    validarDentes(selectedDentes, tratamentoAtual);
  };

  const facesChangeHandler = (event) => {
    const {
      target: { value },
    } = event;
    const selectedFaces = typeof value === "string" ? value.split(",") : value;

    setFaces(selectedFaces);
    validarFaces(selectedFaces, tratamentoAtual);
  };

  const tratamentoChangeHandler = (e) => {
    const novoTratamentoId = e.target.value;

    // Se a entrada já foi recebida, não permitir alteração
    if (dados.entrada && dados.entrada.entradaRecebida) {
      toast.error(
        "Não é possível alterar o tratamento pois o pagamento já foi processado"
      );
      return;
    }

    // Se está a mudar para um tratamento diferente do original
    if (novoTratamentoId !== (dados.tratamento?._id || "")) {
      const novoTratamento = allTratamentos.find(
        (t) => t._id === novoTratamentoId
      );
      setTratamentoNovo(novoTratamento);

      if (dados.entrada) {
        // Calcular diferença de valor
        const diferenca =
          novoTratamento.preco_particular - tratamentoOriginal.preco_particular;
        setDiferencaValor(diferenca);

        // Mostrar diálogo de confirmação
        setShowConfirmDialog(true);
      } else {
        setSelectedProcedure(novoTratamento._id);
        setTratamentoAtual(novoTratamento);
        configurarCamposDentesEFaces(novoTratamento);
      }
    } else {
      // Voltou ao tratamento original
      setSelectedProcedure(novoTratamentoId);
      setTratamentoAtual(tratamentoOriginal);
      setTratamentoNovo(null);
      setDiferencaValor(0);
    }
  };

  const handleConfirmTratamentoChange = () => {
    setSelectedProcedure(tratamentoNovo._id);
    setTratamentoAtual(tratamentoNovo);
    setShowConfirmDialog(false);

    // Reconfigurar e revalidar dentes e faces para o novo tratamento
    configurarCamposDentesEFaces(tratamentoNovo);
  };

  const handleCancelTratamentoChange = () => {
    setTratamentoNovo(null);
    setDiferencaValor(0);
    setShowConfirmDialog(false);
  };

  const adicionarTratamentoAdicional = () => {
    setTratamentosAdicionais((prev) => [
      ...prev,
      {
        id: Date.now(),
        selectedTratamento: "",
        tratamentoObj: null,
        dentes: [],
        faces: [],
        showDentes: false,
        showFaces: false,
        listaDentes: todosOsDentes,
        erroDentes: "",
        erroFaces: "",
      },
    ]);
  };

  const removerTratamentoAdicional = (id) => {
    setTratamentosAdicionais((prev) => prev.filter((item) => item.id !== id));
  };

  const handleTratamentoAdicionalChange = (id, tratamentoId) => {
    setTratamentosAdicionais((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        if (tratamentoId) {
          const tratamento = allTratamentos.find((t) => t._id === tratamentoId);

          if (tratamento) {
            const dentesEspecificos =
              tratamento.dentesEspecificos &&
              tratamento.dentesEspecificos.trim() !== ""
                ? tratamento.dentesEspecificos.split(", ")
                : [];

            return {
              ...item,
              selectedTratamento: tratamentoId,
              tratamentoObj: tratamento,
              showDentes: tratamento.aceitaDentes,
              showFaces: tratamento.aceitaFaces,
              listaDentes:
                dentesEspecificos.length > 0
                  ? dentesEspecificos
                  : todosOsDentes,
              dentes: dentesEspecificos.length === 1 ? dentesEspecificos : [],
              faces: [],
              erroDentes: "",
              erroFaces: "",
            };
          }
        }
        return {
          ...item,
          selectedTratamento: "",
          tratamentoObj: null,
          showDentes: false,
          showFaces: false,
          dentes: [],
          faces: [],
          listaDentes: todosOsDentes,
          erroDentes: "",
          erroFaces: "",
        };
      })
    );
  };

  const handleDentesAdicionalChange = (id, value) => {
    const selectedDentes = typeof value === "string" ? value.split(",") : value;

    setTratamentosAdicionais((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        // Lógica especial para arcadas
        if (selectedDentes.includes("Arcadas")) {
          return {
            ...item,
            dentes: ["Arcadas"],
            faces: [],
            showFaces: false,
            erroDentes: "",
          };
        } else if (
          selectedDentes.includes("Arcada Superior") ||
          selectedDentes.includes("Arcada Inferior")
        ) {
          const apenasArcadas = selectedDentes.filter(
            (d) => d === "Arcada Superior" || d === "Arcada Inferior"
          );
          return {
            ...item,
            dentes: apenasArcadas,
            faces: [],
            showFaces: false,
            erroDentes: "",
          };
        } else {
          return {
            ...item,
            dentes: selectedDentes,
            showFaces:
              item.tratamentoObj?.aceitaFaces && selectedDentes.length > 0,
            erroDentes: "",
          };
        }
      })
    );
  };

  const handleFacesAdicionalChange = (id, value) => {
    const selectedFaces = typeof value === "string" ? value.split(",") : value;

    setTratamentosAdicionais((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, faces: selectedFaces, erroFaces: "" } : item
      )
    );
  };

  const validarTratamentoAdicional = (item) => {
    const errors = { erroDentes: "", erroFaces: "" };
    const tratamento = item.tratamentoObj;

    if (!tratamento) return errors;

    // Validar dentes
    if (tratamento.aceitaDentes) {
      if (item.dentes.length === 0) {
        errors.erroDentes = "Por favor selecione os dentes a tratar";
      } else if (
        tratamento.multiDentes &&
        item.dentes.length % tratamento.qtdDentes !== 0
      ) {
        errors.erroDentes = `Requer múltiplos de ${tratamento.qtdDentes} dentes. Selecionados: ${item.dentes.length}`;
      }
    }

    // Validar faces
    const dentesQueNaoAceitamFaces = [
      "Arcadas",
      "Arcada Superior",
      "Arcada Inferior",
    ];
    const dentesSaoArcadas = item.dentes.some((d) =>
      dentesQueNaoAceitamFaces.includes(d)
    );

    if (tratamento.aceitaFaces && !dentesSaoArcadas) {
      if (item.faces.length === 0) {
        errors.erroFaces = "Por favor selecione as faces a tratar";
      } else if (
        tratamento.qtdFaces < 5 &&
        item.faces.length !== tratamento.qtdFaces
      ) {
        errors.erroFaces = `Exige exatamente ${tratamento.qtdFaces} face(s). Selecionadas: ${item.faces.length}`;
      }
    }

    return errors;
  };

  const calcularValorTratamento = (tratamento, dentes) => {
    if (!tratamento) return 0;

    let numeroExecucoes = 1;
    if (dentes && dentes.length > 0) {
      const dentesQueNaoContam = [
        "Arcadas",
        "Arcada Superior",
        "Arcada Inferior",
      ];
      const dentesValidos = dentes.filter(
        (d) => !dentesQueNaoContam.includes(d)
      );

      if (dentesValidos.length > 0) {
        if (tratamento.multiDentes) {
          numeroExecucoes = dentesValidos.length / tratamento.qtdDentes;
        } else {
          numeroExecucoes = dentesValidos.length;
        }
      }
    }

    return numeroExecucoes * tratamento.preco_particular;
  };

  const validarFormulario = () => {
    // Se não há tratamento selecionado, não pode executar
    if (!selectedProcedure) {
      toast.error("Por favor selecione um tratamento para executar");
      return false;
    }

    if (!tratamentoAtual) return false;

    const dentesValidos = validarDentes(dentes, tratamentoAtual);
    const facesValidas = validarFaces(faces, tratamentoAtual);

    if (!dentesValidos || !facesValidas) return false;

    // Validar tratamentos adicionais
    let adicionaisValidos = true;
    const adicionaisAtualizados = tratamentosAdicionais.map((item) => {
      if (!item.selectedTratamento) {
        adicionaisValidos = false;
        return { ...item, erroDentes: "Selecione um tratamento" };
      }

      const errors = validarTratamentoAdicional(item);
      if (errors.erroDentes || errors.erroFaces) {
        adicionaisValidos = false;
      }
      return { ...item, ...errors };
    });

    if (!adicionaisValidos) {
      setTratamentosAdicionais(adicionaisAtualizados);
      return false;
    }

    return true;
  };

  const submitHandler = async () => {
    if (!validarFormulario()) {
      toast.error("Por favor corrija os erros antes de continuar");
      return;
    }

    startLoading();
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append(
        "relatorio",
        relatorioRef.current.value ||
          "Tratamento isolado executado com sucesso!"
      );

      formData.append("data_inicio_execucao", dataInicioExecucao.toISOString());
      formData.append("data_fim_execucao", dataFimExecucao.toISOString());
      formData.append("atendido", true);
      formData.append("utilizador", auth.userId);

      // Adicionar dentes e faces do tratamento principal
      if (showDentes && dentes.length > 0) {
        formData.append("dente", dentes.join(","));
      }

      if (showFaces && faces.length > 0) {
        faces.forEach((face) => formData.append("faces", face));
      }

      const houveAlteracao =
        selectedProcedure !== (dados.tratamento?._id || "");

      // Se houve seleção/alteração de tratamento
      if (houveAlteracao) {
        const tratamentoExecutado = allTratamentos.find(
          (t) => t._id === selectedProcedure
        );

        // Criar texto de alteração com informação de dentes e faces
        let textoAlteracao = dados.entrada
          ? `[ALTERAÇÃO DE TRATAMENTO]\n` +
            `Original: ${
              tratamentoOriginal.designacao
            } (${tratamentoOriginal.preco_particular.toLocaleString(
              "pt-Br"
            )}CVE)\n` +
            `Executado: ${
              tratamentoExecutado.designacao
            } (${tratamentoExecutado.preco_particular.toLocaleString(
              "pt-Br"
            )}CVE)\n` +
            `Diferença: ${
              diferencaValor > 0 ? "+" : ""
            }${diferencaValor.toLocaleString("pt-Br")}CVE\n`
          : `[SELEÇÃO DE TRATAMENTO]\n` +
            `Categoria Original: ${
              dados.categoriaTratamento?.categoria || "N/A"
            }\n` +
            `Tratamento Selecionado: ${
              tratamentoExecutado.designacao
            } (${tratamentoExecutado.preco_particular.toLocaleString(
              "pt-Br"
            )}CVE)\n`;

        if (showDentes && dentes.length > 0) {
          textoAlteracao += `Dente(s): ${dentes.join(", ")}\n`;
        }
        if (showFaces && faces.length > 0) {
          textoAlteracao += `Face(s): ${faces.join(", ")}\n`;
        }

        textoAlteracao +=
          `Data/Hora: ${moment().format("DD/MM/YYYY HH:mm")}\n` +
          `Alterado por: ${auth.nome || auth.userId}`;

        formData.append("alteracaoTratamento", textoAlteracao);
        formData.append("tratamento", selectedProcedure);

        if (dados.entrada) {
          let valorFinal;
          let numeroExecucoes = 1;

          if (dentes.length > 0) {
            if (tratamentoExecutado.multiDentes) {
              numeroExecucoes = dentes.length / tratamentoExecutado.qtdDentes;
              valorFinal =
                numeroExecucoes * tratamentoExecutado.preco_particular;
            } else {
              numeroExecucoes = dentes.length;
              valorFinal =
                numeroExecucoes * tratamentoExecutado.preco_particular;
            }
          } else {
            valorFinal = tratamentoExecutado.preco_particular;
          }

          formData.append("novoValorEntrada", valorFinal);
        }
      }

      // Adicionar tratamentos adicionais
      if (tratamentosAdicionais.length > 0) {
        const adicionaisData = tratamentosAdicionais.map((item) => ({
          tratamento: item.selectedTratamento,
          dente: item.dentes.length > 0 ? item.dentes.join(",") : undefined,
          faces: item.faces.length > 0 ? item.faces : undefined,
        }));
        formData.append(
          "tratamentosAdicionais",
          JSON.stringify(adicionaisData)
        );
      }

      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentoIsolados/executar/${dados.id}`,
          "PATCH",
          formData,
          {
            Authorization: "Bearer " + auth.token,
          }
        );
        toast.success(
          tratamentosAdicionais.length > 0
            ? `${
                tratamentosAdicionais.length + 1
              } tratamento(s) executado(s) com sucesso!`
            : "Tratamento isolado executado com sucesso!"
        );

        // Passar informação sobre adicionais criados
        if (houveAlteracao) {
          handleModalSubmitSuccess(
            dados.id,
            {
              id: tratamentoAtual._id,
              _id: tratamentoAtual._id,
              designacao: tratamentoAtual.designacao,
            },
            response.tratamentosAdicionaisCriados || []
          );
        } else {
          handleModalSubmitSuccess(
            dados.id,
            null,
            response.tratamentosAdicionaisCriados || []
          );
        }
      } catch (err) {
        console.error("err", err);
        toast.error("Erro ao executar tratamento");
        return;
      }

      handleCloseModal();
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
      setIsSubmitting(false);
    }
  };

  const preSubmitHandler = () => {
    submitHandler();
  };

  // Verificar se o select deve estar desabilitado
  const isTratamentoSelectDisabled =
    dados.entrada && dados.entrada.entradaRecebida;

  return (
    <>
      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="fluxo-atendimento__modal__box" id="dividas__modal">
          <h1 className="fluxo-atendimento__modal__titulo">
            Executar Tratamento Isolado - {dados.nome}
          </h1>

          {/* Mesma nota da criação na ficha do paciente: é aqui, com o doente
              na cadeira, que se decide executar por fora do orçamento — e é
              aqui que o engano custa a execução do plano. */}
          <NotaInformativa
            titulo="Tratamento isolado"
            style={{ margin: "0 0 18px" }}
          >
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              <li>
                Um tratamento isolado{" "}
                <strong>NÃO PERTENCE AO ORÇAMENTO DO CLIENTE</strong>.
              </li>
              <li>
                Se este tratamento já está num orçamento do cliente, feche esta
                janela: aprove o orçamento e execute-o pelo{" "}
                <strong>plano de tratamento</strong>. Executado por aqui, o
                orçamento fica por cumprir com o trabalho já feito.
              </li>
            </ul>
          </NotaInformativa>

          <div className="modal-executar-procedimentos__datetimeContainer">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                className="planos-tratamento__modal__datePicker planos-tratamento__modal__datePicker--1"
                label="Data/Hora Início"
                inputFormat="DD-MM-YYYY HH:mm"
                value={dataInicioExecucao}
                onChange={(value) => setDataInicioExecucao(value)}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                className="planos-tratamento__modal__datePicker planos-tratamento__modal__datePicker--2"
                label="Data/Hora Fim"
                inputFormat="DD-MM-YYYY HH:mm"
                value={dataFimExecucao}
                onChange={(value) => setDataFimExecucao(value)}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </div>

          <div style={{ marginTop: "20px", marginBottom: "20px" }}>
            {dados.categoriaTratamento && !dados.tratamento && (
              <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                <AlertTitle>Categoria de Tratamento</AlertTitle>
                <strong>Categoria:</strong>{" "}
                {dados.categoriaTratamento.categoria}
                <br />
                <em>
                  Este tratamento isolado foi criado apenas com categoria.
                  Selecione um tratamento específico abaixo para executar.
                </em>
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel id="tratamento-select-label">Tratamento*</InputLabel>
              <Select
                labelId="tratamento-select-label"
                id="tratamento-select"
                value={selectedProcedure}
                onChange={tratamentoChangeHandler}
                disabled={isTratamentoSelectDisabled}
                label="Tratamento*"
              >
                <MenuItem value="">Selecione um tratamento</MenuItem>
                {tratamentos.map((procedure, index) => (
                  <MenuItem value={procedure.id} key={index}>
                    {procedure.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Aviso se entrada já foi recebida */}
            {dados.entrada && dados.entrada.entradaRecebida && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <AlertTitle>Tratamento bloqueado</AlertTitle>O pagamento já foi
                processado. Não é possível alterar o tratamento.
              </Alert>
            )}
          </div>

          {/* Campo de Dentes */}
          {showDentes && (
            <FormControl fullWidth sx={{ mb: 2 }} error={!!erroDentes}>
              <InputLabel id="dentes-label">Dentes*</InputLabel>
              <Select
                labelId="dentes-label"
                multiple
                value={dentes}
                onChange={dentesChangeHandler}
                input={<OutlinedInput label="Dentes*" />}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={MenuProps}
              >
                {listaDentes.map((dente) => (
                  <MenuItem key={dente} value={dente}>
                    <Checkbox checked={dentes.indexOf(dente) > -1} />
                    <ListItemText primary={dente} />
                  </MenuItem>
                ))}
              </Select>
              {erroDentes && (
                <span
                  style={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    marginTop: "3px",
                    display: "block",
                  }}
                >
                  {erroDentes}
                </span>
              )}
            </FormControl>
          )}

          {/* Campo de Faces */}
          {showFaces && (
            <FormControl fullWidth sx={{ mb: 2 }} error={!!erroFaces}>
              <InputLabel id="faces-label">Faces*</InputLabel>
              <Select
                labelId="faces-label"
                multiple
                value={faces}
                onChange={facesChangeHandler}
                input={<OutlinedInput label="Faces*" />}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={MenuProps}
              >
                {facesDentes.map((face) => (
                  <MenuItem key={face} value={face}>
                    <Checkbox checked={faces.indexOf(face) > -1} />
                    <ListItemText primary={face} />
                  </MenuItem>
                ))}
              </Select>
              {erroFaces && (
                <span
                  style={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    marginTop: "3px",
                    display: "block",
                  }}
                >
                  {erroFaces}
                </span>
              )}
            </FormControl>
          )}

          {selectedProcedure &&
            tratamentoAtual &&
            dentes.length > 0 &&
            !dentes.some((d) =>
              ["Arcadas", "Arcada Superior", "Arcada Inferior"].includes(d)
            ) &&
            (() => {
              const numeroExecucoes = tratamentoAtual.multiDentes
                ? dentes.length / tratamentoAtual.qtdDentes
                : dentes.length;

              return numeroExecucoes > 1 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Múltiplas Execuções</AlertTitle>
                  Será executado:{" "}
                  <strong>
                    {numeroExecucoes}× {tratamentoAtual.designacao}
                  </strong>
                  <br />
                  Dentes: <strong>{dentes.join(", ")}</strong>
                  {tratamentoAtual.multiDentes && (
                    <>
                      <br />
                      <em>
                        ({numeroExecucoes} grupos de {tratamentoAtual.qtdDentes}{" "}
                        dentes)
                      </em>
                    </>
                  )}
                  {dados.entrada && (
                    <>
                      <br />
                      Valor total:{" "}
                      <strong>
                        {(
                          tratamentoAtual.preco_particular * numeroExecucoes
                        ).toLocaleString("pt-Br")}{" "}
                        CVE
                      </strong>
                      <br />
                      <em>
                        ({numeroExecucoes} ×{" "}
                        {tratamentoAtual.preco_particular.toLocaleString(
                          "pt-Br"
                        )}{" "}
                        CVE)
                      </em>
                    </>
                  )}
                </Alert>
              ) : null;
            })()}

          <Box sx={{ mt: 3, mb: 2 }}>
            <h3 style={{ marginBottom: 16, fontSize: "1rem", fontWeight: 500 }}>
              Tratamentos Adicionais
            </h3>

            {tratamentosAdicionais.map((item, index) => (
              <Box
                key={item.id}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  backgroundColor: "#fafafa",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <strong style={{ fontSize: "0.9rem" }}>
                    Adicional {index + 1}
                  </strong>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => removerTratamentoAdicional(item.id)}
                    sx={{ textTransform: "none" }}
                  >
                    Remover
                  </Button>
                </div>

                <FormControl
                  fullWidth
                  sx={{ mb: 2 }}
                  error={!!item.erroDentes && !item.selectedTratamento}
                >
                  <InputLabel id={`tratamento-adicional-label-${item.id}`}>
                    Tratamento*
                  </InputLabel>
                  <Select
                    labelId={`tratamento-adicional-label-${item.id}`}
                    value={item.selectedTratamento}
                    onChange={(e) =>
                      handleTratamentoAdicionalChange(item.id, e.target.value)
                    }
                    label="Tratamento*"
                  >
                    <MenuItem value="">Selecione um tratamento</MenuItem>
                    {allTratamentos
                      .filter((t) => t.ativo)
                      .map((tratamento) => (
                        <MenuItem key={tratamento._id} value={tratamento._id}>
                          {tratamento.designacao}
                          {tratamento.fornecedor?.nome
                            ? ` (${tratamento.fornecedor.nome})`
                            : ""}{" "}
                          -{" "}
                          {tratamento.preco_particular.toLocaleString("pt-Br")}{" "}
                          CVE
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {item.showDentes && (
                  <FormControl
                    fullWidth
                    sx={{ mb: 2 }}
                    error={!!item.erroDentes}
                  >
                    <InputLabel id={`dentes-adicional-label-${item.id}`}>
                      Dentes*
                    </InputLabel>
                    <Select
                      labelId={`dentes-adicional-label-${item.id}`}
                      multiple
                      value={item.dentes}
                      onChange={(e) =>
                        handleDentesAdicionalChange(item.id, e.target.value)
                      }
                      input={<OutlinedInput label="Dentes*" />}
                      renderValue={(selected) => selected.join(", ")}
                      MenuProps={MenuProps}
                    >
                      {item.listaDentes.map((dente) => (
                        <MenuItem key={dente} value={dente}>
                          <Checkbox checked={item.dentes.indexOf(dente) > -1} />
                          <ListItemText primary={dente} />
                        </MenuItem>
                      ))}
                    </Select>
                    {item.erroDentes && (
                      <span
                        style={{
                          color: "#d32f2f",
                          fontSize: "0.75rem",
                          marginTop: "3px",
                          display: "block",
                        }}
                      >
                        {item.erroDentes}
                      </span>
                    )}
                  </FormControl>
                )}

                {item.showFaces &&
                  !item.dentes.some((d) =>
                    ["Arcadas", "Arcada Superior", "Arcada Inferior"].includes(
                      d
                    )
                  ) && (
                    <FormControl
                      fullWidth
                      sx={{ mb: 2 }}
                      error={!!item.erroFaces}
                    >
                      <InputLabel id={`faces-adicional-label-${item.id}`}>
                        Faces*
                      </InputLabel>
                      <Select
                        labelId={`faces-adicional-label-${item.id}`}
                        multiple
                        value={item.faces}
                        onChange={(e) =>
                          handleFacesAdicionalChange(item.id, e.target.value)
                        }
                        input={<OutlinedInput label="Faces*" />}
                        renderValue={(selected) => selected.join(", ")}
                        MenuProps={MenuProps}
                      >
                        {facesDentes.map((face) => (
                          <MenuItem key={face} value={face}>
                            <Checkbox checked={item.faces.indexOf(face) > -1} />
                            <ListItemText primary={face} />
                          </MenuItem>
                        ))}
                      </Select>
                      {item.erroFaces && (
                        <span
                          style={{
                            color: "#d32f2f",
                            fontSize: "0.75rem",
                            marginTop: "3px",
                            display: "block",
                          }}
                        >
                          {item.erroFaces}
                        </span>
                      )}
                    </FormControl>
                  )}

                {/* Valor do tratamento adicional */}
                {item.tratamentoObj && dados.entrada && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Valor:{" "}
                    <strong>
                      {calcularValorTratamento(
                        item.tratamentoObj,
                        item.dentes
                      ).toLocaleString("pt-Br")}{" "}
                      CVE
                    </strong>
                  </Alert>
                )}
              </Box>
            ))}

            <Button
              variant="outlined"
              onClick={adicionarTratamentoAdicional}
              sx={{ textTransform: "none" }}
              fullWidth
            >
              + Adicionar outro tratamento
            </Button>
          </Box>

          {/* Resumo Financeiro */}
          {dados.entrada &&
            (tratamentosAdicionais.length > 0 || selectedProcedure) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Resumo Financeiro</AlertTitle>
                <div style={{ fontSize: "0.9rem" }}>
                  <div>
                    <strong>Tratamento Principal:</strong>{" "}
                    {tratamentoAtual
                      ? `${
                          tratamentoAtual.designacao
                        } - ${calcularValorTratamento(
                          tratamentoAtual,
                          dentes
                        ).toLocaleString("pt-Br")} CVE`
                      : "Não selecionado"}
                  </div>
                  {tratamentosAdicionais.length > 0 && (
                    <>
                      <div style={{ marginTop: 8 }}>
                        <strong>Tratamentos Adicionais:</strong>
                      </div>
                      {tratamentosAdicionais.map((item, index) => (
                        <div key={item.id} style={{ marginLeft: 16 }}>
                          •{" "}
                          {item.tratamentoObj?.designacao || "Não selecionado"}{" "}
                          -{" "}
                          {item.tratamentoObj
                            ? `${calcularValorTratamento(
                                item.tratamentoObj,
                                item.dentes
                              ).toLocaleString("pt-Br")} CVE`
                            : "0 CVE"}
                        </div>
                      ))}
                    </>
                  )}
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 8,
                      borderTop: "1px solid #ccc",
                      fontWeight: "bold",
                    }}
                  >
                    Total:{" "}
                    {(
                      calcularValorTratamento(tratamentoAtual, dentes) +
                      tratamentosAdicionais.reduce(
                        (acc, item) =>
                          acc +
                          calcularValorTratamento(
                            item.tratamentoObj,
                            item.dentes
                          ),
                        0
                      )
                    ).toLocaleString("pt-Br")}{" "}
                    CVE
                  </div>
                </div>
              </Alert>
            )}

          <div className="adicionarEvolucaoContainer">
            <textarea
              className="executarProcedimento__container__textarea"
              placeholder="Escreva aqui o relatório do tratamento isolado."
              cols="30"
              rows="5"
              ref={relatorioRef}
            />
          </div>

          <div
            style={{
              gridColumn: "1/-1",
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "25px",
            }}
          >
            <span
              className="blue-button"
              onClick={() => preSubmitHandler()}
              style={{
                marginRight: "10px",
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
            >
              {isSubmitting ? "Guardando..." : "Executar"}
            </span>

            <span className="cancel-btn" onClick={handleCloseModal}>
              Fechar
            </span>
          </div>
        </Box>
      </Modal>

      {/* Diálogo de confirmação para alteração de tratamento */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelTratamentoChange}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirmar Alteração de Tratamento
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <strong>Tratamento Original:</strong>{" "}
            {tratamentoOriginal?.designacao} (
            {tratamentoOriginal?.preco_particular.toLocaleString("pt-Br")}CVE)
            <br />
            <strong>Novo Tratamento:</strong> {tratamentoNovo?.designacao} (
            {tratamentoNovo?.preco_particular.toLocaleString("pt-Br")}CVE)
            <br />
            <strong>Diferença:</strong>
            <span
              style={{
                color: diferencaValor > 0 ? "#2e7d32" : "#d32f2f",
                fontWeight: "bold",
              }}
            >
              {diferencaValor > 0 ? " +" : " "}
              {Math.abs(diferencaValor).toLocaleString("pt-Br")}CVE
            </span>
            <br />
            <br />
            {tratamentoNovo && (
              <Alert severity="info">
                <strong>Requisitos do novo tratamento:</strong>
                <br />
                {tratamentoNovo.aceitaDentes && (
                  <>
                    • Requer seleção de dente(s)
                    {tratamentoNovo.multiDentes &&
                      ` (exatamente ${tratamentoNovo.qtdDentes})`}
                    <br />
                  </>
                )}
                {tratamentoNovo.aceitaFaces && (
                  <>
                    • Requer seleção de face(s)
                    {tratamentoNovo.qtdFaces < 5 &&
                      ` (exatamente ${tratamentoNovo.qtdFaces})`}
                  </>
                )}
                {!tratamentoNovo.aceitaDentes &&
                  !tratamentoNovo.aceitaFaces &&
                  "• Não requer seleção de dentes ou faces"}
              </Alert>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTratamentoChange} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmTratamentoChange}
            color="primary"
            autoFocus
          >
            Confirmar Alteração
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ModalExecutarTratamentoIsolado;
